import React, { useState } from 'react';
import { Table } from 'antd';
import { CampaignPerformanceItem } from '../types/performance.types';

interface CampaignPerformanceTableProps {
  data: CampaignPerformanceItem[];
  loading: boolean;
  error: Error | null;
}

const CampaignPerformanceTable: React.FC<CampaignPerformanceTableProps> = ({
  data,
  loading,
  error,
}) => {
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(100);

  // Helper functions
  const rate = (total: number, fraction: number): string | number => {
    if (total === 0) return 'No Data';
    const percentage = (fraction / total) * 100;
    return Math.round(percentage);
  };

  const cpa = (totalCost: number, totalSales: number): string | number => {
    if (totalSales === 0) return 'No Data';
    const average = totalCost / totalSales;
    return Math.round(average);
  };

  const columns = [
    {
      title: 'Vendor Name',
      dataIndex: 'vendorName',
      key: 'vendorName',
      sorter: (a: CampaignPerformanceItem, b: CampaignPerformanceItem) =>
        a.vendorName.localeCompare(b.vendorName),
    },
    {
      title: 'Bill Rate',
      key: 'billRate',
      render: (_: any, record: CampaignPerformanceItem) =>
        record.totalLeads ? `${rate(record.totalLeads, record.totalBillableLeads)}%` : 'No Data',
      sorter: (a: CampaignPerformanceItem, b: CampaignPerformanceItem) => {
        const rateA =
          a.totalLeads > 0
            ? (a.totalBillableLeads / a.totalLeads) * 100
            : 0;
        const rateB =
          b.totalLeads > 0
            ? (b.totalBillableLeads / b.totalLeads) * 100
            : 0;
        return rateA - rateB;
      },
    },
    {
      title: 'CPA-A',
      key: 'cpaA',
      render: (_: any, record: CampaignPerformanceItem) => {
        const cpaValue = cpa(record.totalCost, record.totalU65Sales);
        const isGood = typeof cpaValue === 'number' && cpaValue < 300;
        return (
          <span style={{ color: isGood ? '#A1E3CB' : '#FF000094' }}>
            {typeof cpaValue === 'number' ? `$${cpaValue}` : cpaValue}
          </span>
        );
      },
      sorter: (a: CampaignPerformanceItem, b: CampaignPerformanceItem) => {
        const cpaA = cpa(a.totalCost, a.totalU65Sales);
        const cpaB = cpa(b.totalCost, b.totalU65Sales);
        if (typeof cpaA === 'number' && typeof cpaB === 'number') {
          return cpaA - cpaB;
        }
        return 0;
      },
    },
    {
      title: 'CPA-B',
      key: 'cpaB',
      render: (_: any, record: CampaignPerformanceItem) => {
        const totalSales = record.totalU65Sales + record.totalACASales;
        const cpaValue = cpa(record.totalCost, totalSales);
        const isGood = typeof cpaValue === 'number' && cpaValue < 100;
        return (
          <span style={{ color: isGood ? '#A1E3CB' : '#FF000094' }}>
            {typeof cpaValue === 'number' ? `$${cpaValue}` : cpaValue}
          </span>
        );
      },
      sorter: (a: CampaignPerformanceItem, b: CampaignPerformanceItem) => {
        const cpaA = cpa(a.totalCost, a.totalU65Sales + a.totalACASales);
        const cpaB = cpa(b.totalCost, b.totalU65Sales + b.totalACASales);
        if (typeof cpaA === 'number' && typeof cpaB === 'number') {
          return cpaA - cpaB;
        }
        return 0;
      },
    },
    {
      title: 'Total Calls',
      dataIndex: 'totalLeads',
      key: 'totalLeads',
      sorter: (a: CampaignPerformanceItem, b: CampaignPerformanceItem) =>
        a.totalLeads - b.totalLeads,
    },
    {
      title: 'Q Calls',
      dataIndex: 'totalBillableLeads',
      key: 'totalBillableLeads',
      sorter: (a: CampaignPerformanceItem, b: CampaignPerformanceItem) =>
        a.totalBillableLeads - b.totalBillableLeads,
    },
    {
      title: 'Marketing Cost',
      dataIndex: 'totalCost',
      key: 'totalCost',
      render: (_: any, record: CampaignPerformanceItem) =>
        `$${record.totalCost.toLocaleString('en-US', { minimumFractionDigits: 2 })}`,
      sorter: (a: CampaignPerformanceItem, b: CampaignPerformanceItem) =>
        a.totalCost - b.totalCost,
    },
    {
      title: 'Cost Per Call',
      key: 'cpc',
      render: (_: any, record: CampaignPerformanceItem) => {
        const cpc = record.totalBillableLeads > 0
          ? (record.totalCost / record.totalBillableLeads).toFixed(2)
          : 'No Data';
        return `$${cpc}`;
      },
      sorter: (a: CampaignPerformanceItem, b: CampaignPerformanceItem) => {
        const cpcA = a.totalBillableLeads > 0 ? a.totalCost / a.totalBillableLeads : 0;
        const cpcB = b.totalBillableLeads > 0 ? b.totalCost / b.totalBillableLeads : 0;
        return cpcA - cpcB;
      },
    },
    {
      title: 'ACA',
      dataIndex: 'totalACASales',
      key: 'totalACASales',
      sorter: (a: CampaignPerformanceItem, b: CampaignPerformanceItem) =>
        a.totalACASales - b.totalACASales,
    },
    {
      title: 'U65',
      dataIndex: 'totalU65Sales',
      key: 'totalU65Sales',
      sorter: (a: CampaignPerformanceItem, b: CampaignPerformanceItem) =>
        a.totalU65Sales - b.totalU65Sales,
    },
    {
      title: 'Ancillaries',
      dataIndex: 'totalAncillaries',
      key: 'totalAncillaries',
      sorter: (a: CampaignPerformanceItem, b: CampaignPerformanceItem) =>
        a.totalAncillaries - b.totalAncillaries,
    },
    {
      title: 'Premium',
      key: 'premium',
      render: (_: any, record: CampaignPerformanceItem) => {
        const totalPremium = record.totalPrivateMedPremium + record.totalAncillariesPremium;
        const maxPremium = 300;
        const percentage = Math.min((totalPremium / maxPremium) * 100, 100);

        return (
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <span>${totalPremium.toFixed(2).toLocaleString()}</span>
            <div
              style={{
                width: 200,
                height: 20,
                position: 'relative',
                borderRadius: '8px',
                overflow: 'hidden',
                backgroundColor: '#f0f0f0',
              }}
            >
              <div
                style={{
                  width: `${percentage}%`,
                  height: '100%',
                  backgroundColor: '#A1E3CB',
                  transition: 'width 0.3s ease',
                  borderRadius: '8px',
                }}
              />
            </div>
          </div>
        );
      },
      sorter: (a: CampaignPerformanceItem, b: CampaignPerformanceItem) => {
        const premiumA = a.totalPrivateMedPremium + a.totalAncillariesPremium;
        const premiumB = b.totalPrivateMedPremium + b.totalAncillariesPremium;
        return premiumA - premiumB;
      },
    },
    {
      title: 'CR',
      key: 'cr',
      render: (_: any, record: CampaignPerformanceItem) => {
        const totalSales = record.totalACASales + record.totalU65Sales;
        const crValue = rate(record.totalBillableLeads, totalSales);
        const isGood = typeof crValue === 'number' && crValue > 60;
        return (
          <span style={{ color: isGood ? '#A1E3CB' : '#FF000094' }}>
            {typeof crValue === 'number' ? `${crValue}%` : crValue}
          </span>
        );
      },
      sorter: (a: CampaignPerformanceItem, b: CampaignPerformanceItem) => {
        const crA = rate(a.totalBillableLeads, a.totalACASales + a.totalU65Sales);
        const crB = rate(b.totalBillableLeads, b.totalACASales + b.totalU65Sales);
        if (typeof crA === 'number' && typeof crB === 'number') {
          return crA - crB;
        }
        return 0;
      },
    },
  ];

  if (error) {
    return (
      <div style={{ color: 'red', padding: '20px' }}>
        Error loading data: {error.message}
      </div>
    );
  }

  return (
    <Table
      columns={columns}
      dataSource={data}
      rowKey={(record) => `${record.queueId}`}
      loading={loading}
      pagination={{
        current: currentPage,
        pageSize: pageSize,
        pageSizeOptions: ['10', '20', '50', '100'],
        showSizeChanger: true,
        onShowSizeChange: (current, size) => setPageSize(size),
        onChange: (page) => setCurrentPage(page),
        total: data.length,
      }}
    />
  );
};

export default CampaignPerformanceTable;
